import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { components, MenuListProps, StylesConfig } from 'react-select'
import { useAppDispatch } from 'src/redux/storeHooks'
import { getPackageListAsync } from 'src/redux/actions/packageActions'

// ── Types ──

type PresetOption = {
  value: string | '__new__'
  label: string
  presetData?: any // The full package preset object
  isNew?: boolean
  isDisabled?: boolean
}

type PackageDimensionSelectProps = {
  value: any | null | '__new__'
  onChange: (preset: any | '__new__') => void
  disabled?: boolean
  extractDimensionsFromPreset: (preset: any) => any
}

// ── Constants ──

const NEW_OPTION: PresetOption = { value: '__new__', label: '+ Add New Dimension...', isNew: true }
const SEARCH_DEBOUNCE_MS = 400

// ── Helpers ──

const isAbortedRequest = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

// ── Paginated Menu List (scroll position preservation) ──

type ScrollInternals = {
  menuListNodeRef: React.MutableRefObject<HTMLDivElement | null>
  savedScrollTopRef: React.MutableRefObject<number>
  pendingScrollRestoreRef: React.MutableRefObject<number | null>
  onMenuScroll: (event: React.UIEvent<HTMLDivElement>) => void
}

const ScrollInternalsContext = createContext<ScrollInternals | null>(null)

const PaginatedMenuList = (props: MenuListProps<PresetOption, false>) => {
  const internals = useContext(ScrollInternalsContext)
  const { ref: menuRef, ...menuInnerProps } = props.innerProps

  useLayoutEffect(() => {
    if (!internals || internals.pendingScrollRestoreRef.current === null) return
    const scrollTop = internals.pendingScrollRestoreRef.current
    const menuNode = internals.menuListNodeRef.current
    if (!menuNode) return
    menuNode.scrollTop = scrollTop
    internals.savedScrollTopRef.current = scrollTop
    if (Math.abs(menuNode.scrollTop - scrollTop) < 2) {
      internals.pendingScrollRestoreRef.current = null
    }
  }, [internals, props.children])

  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...menuInnerProps,
        ref: (node: HTMLDivElement | null) => {
          if (internals) internals.menuListNodeRef.current = node
          if (typeof menuRef === 'function') menuRef(node)
          else if (menuRef && typeof menuRef === 'object') {
            ;(menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node
          }
        },
        onScroll: (event) => {
          if (internals) internals.savedScrollTopRef.current = event.currentTarget.scrollTop
          menuInnerProps?.onScroll?.(event)
          if (internals) internals.onMenuScroll(event as React.UIEvent<HTMLDivElement>)
        },
      }}
    >
      {props.children}
    </components.MenuList>
  )
}

// ── Custom Styles ──

const selectStyles: StylesConfig<PresetOption, false> = {
  control: (base, state) => ({
    ...base,
    fontSize: '14px',
    minHeight: '34px',
    height: '34px',
    borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px 8px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px 8px',
  }),
  input: (base) => ({
    ...base,
    margin: '0',
    padding: '0',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: '14px',
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    maxHeight: '200px', // Fixed height so it doesn't go to screen bottom
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isDisabled
      ? 'transparent'
      : state.isSelected
      ? '#0d6efd'
      : state.isFocused
      ? '#e9ecef'
      : state.data?.isNew
      ? '#fff'
      : 'transparent',
    color: state.isDisabled ? '#6c757d' : state.isSelected ? '#fff' : state.data?.isNew ? '#0d6efd' : '#212529',
    borderBottom: state.data?.isNew ? '1px solid #dee2e6' : 'none',
    fontWeight: state.data?.isNew ? 600 : 'normal',
    position: state.data?.isNew ? 'sticky' : undefined,
    top: state.data?.isNew ? 0 : undefined,
    zIndex: state.data?.isNew ? 10 : undefined,
    '&:active': { backgroundColor: state.isDisabled ? 'transparent' : '#0d6efd', color: state.isDisabled ? '#6c757d' : '#fff' },
  }),
  placeholder: (base) => ({ ...base, color: '#adb5bd', opacity: 0.6 }),
  singleValue: (base) => ({ ...base, color: '#212529' }),
  indicatorSeparator: () => ({ display: 'none' }),
}

// ── Main Component ──

const PackageDimensionSelect: FC<PackageDimensionSelectProps> = ({
  value,
  onChange,
  disabled = false,
  extractDimensionsFromPreset,
}) => {
  const dispatch = useAppDispatch()

  const [options, setOptions] = useState<PresetOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paginationAbortRef = useRef<AbortController | null>(null)
  const activeSearchRef = useRef('')
  const nextUrlRef = useRef<string | null>(null)
  const isLoadingMoreRef = useRef(false)
  const pageRef = useRef(1)

  const menuListNodeRef = useRef<HTMLDivElement | null>(null)
  const savedScrollTopRef = useRef(0)
  const pendingScrollRestoreRef = useRef<number | null>(null)

  const fetchPresets = useCallback(
    async (searchQuery: string, page: number, isLoadMore = false) => {
      if (paginationAbortRef.current) {
        paginationAbortRef.current.abort()
      }
      const abortController = new AbortController()
      paginationAbortRef.current = abortController

      if (!isLoadMore) {
        setIsLoading(true)
        nextUrlRef.current = null
        pageRef.current = 1
      } else {
        isLoadingMoreRef.current = true
      }

      try {
        const response: any = await dispatch(
          getPackageListAsync({
            page,
            limit: 20,
            search: searchQuery || undefined,
          })
        )

        // Toolkit injects the AbortSignal, check if it was aborted
        if (abortController.signal.aborted) return

        const payload = response.payload
        const results = payload?.results?.data || payload?.data || []
        
        // Update next pagination url
        const next = payload?.next || payload?.results?.next
        nextUrlRef.current = typeof next === 'string' && next.length > 0 ? next : null

        const mappedOptions: PresetOption[] = results.map((preset: any) => {
          const pId = preset.additional_info?.[0]?.platformfielddata ?? preset.id
          const dims = extractDimensionsFromPreset(preset)
          const label = `${preset.api_name} (${dims.packageLength} x ${dims.packageWidth} x ${dims.packageHeight})`
          return {
            value: String(pId),
            label,
            presetData: preset,
          }
        })

        setOptions((prev) => {
          const newOpts = isLoadMore ? [...prev, ...mappedOptions] : mappedOptions
          // Filter duplicates just in case
          const seen = new Set<string>()
          return newOpts.filter((opt) => {
            if (seen.has(opt.value)) return false
            seen.add(opt.value)
            return true
          })
        })
        
        if (isLoadMore) {
          pendingScrollRestoreRef.current = savedScrollTopRef.current
        }

      } catch (err: any) {
        if (!isAbortedRequest(err)) {
          console.error('Failed to fetch package presets', err)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
          isLoadingMoreRef.current = false
        }
      }
    },
    [dispatch, extractDimensionsFromPreset]
  )

  useEffect(() => {
    fetchPresets('', 1)
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
      paginationAbortRef.current?.abort()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (newVal: string, { action }: any) => {
    if (action === 'input-change') {
      setInputValue(newVal)
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = setTimeout(() => {
        activeSearchRef.current = newVal
        fetchPresets(newVal, 1)
      }, SEARCH_DEBOUNCE_MS)
    } else if (action === 'set-value' || action === 'menu-close' || action === 'input-blur') {
      setInputValue('')
      if (activeSearchRef.current !== '') {
        activeSearchRef.current = ''
        fetchPresets('', 1)
      }
    }
  }

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      if (
        target.scrollHeight - target.scrollTop <= target.clientHeight + 10 &&
        nextUrlRef.current &&
        !isLoadingMoreRef.current &&
        !isLoading
      ) {
        pageRef.current += 1
        fetchPresets(activeSearchRef.current, pageRef.current, true)
      }
    },
    [fetchPresets, isLoading]
  )

  const selectedOption = useMemo(() => {
    if (value === null) return null
    if (value === '__new__') return NEW_OPTION
    
    const presetId = String(value.isHydratedFallback ? value.id : (value.additional_info?.[0]?.platformfielddata ?? value.id))
    const found = options.find((o) => o.value === presetId)
    if (found) return found
    
    // Fallback if not in options
    if (value.isHydratedFallback) {
      return {
        value: presetId,
        label: `${value.api_name} (${value.packageLength} x ${value.packageWidth} x ${value.packageHeight})`,
        presetData: value
      }
    } else {
      const dims = extractDimensionsFromPreset(value)
      return {
        value: presetId,
        label: `${value.api_name || value.name} (${dims.packageLength} x ${dims.packageWidth} x ${dims.packageHeight})`,
        presetData: value
      }
    }
  }, [value, options, extractDimensionsFromPreset])

  const scrollInternals = useMemo(
    (): ScrollInternals => ({
      menuListNodeRef,
      savedScrollTopRef,
      pendingScrollRestoreRef,
      onMenuScroll: handleMenuScroll,
    }),
    [handleMenuScroll]
  )

  return (
    <ScrollInternalsContext.Provider value={scrollInternals}>
      <Select<PresetOption, false>
        isDisabled={disabled}
        isLoading={isLoading}
        value={selectedOption}
        options={[
          NEW_OPTION,
          ...options,
          ...(options.length === 0 && inputValue && !isLoading
            ? [{ value: '__nomatch__', label: 'No match found!', isDisabled: true }]
            : []),
        ]}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        onChange={(option) => {
          if (!option) return
          if (option.isNew) {
            onChange('__new__')
          } else {
            onChange(option.presetData)
          }
        }}
        components={{ MenuList: PaginatedMenuList }}
        styles={selectStyles}
        placeholder='Search existing dimension...'
        noOptionsMessage={({ inputValue }) => 
          isLoading ? 'Loading...' : (inputValue ? 'No match found!' : 'No packages found!')
        }
        isClearable={false}
        filterOption={null} // Disable client-side filtering
      />
    </ScrollInternalsContext.Provider>
  )
}

export default PackageDimensionSelect
